import {
  differenceInDays,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  parseISO,
} from "date-fns";
import parsePhoneNumber from "libphonenumber-js";

import {
  CandidateCautionReason,
  CandidateStatus,
  type CandidateFullResponse,
  type CandidatePatchRequest,
  type CandidateRequest,
  type CandidateWithContactStatusResponse,
} from "../api";
import { Evaluation } from "./evaluation";
import { Utility } from "./utility";

export interface Candidate extends CandidateWithContactStatusResponse {
  get_local_time(time_string: string): Date;
}
export interface CandidateFull extends CandidateFullResponse {
  evaluation: Evaluation | null;
}

export const CandidateCautionReasonExplanations: {
  [reason in CandidateCautionReason]: string;
} = {
  [CandidateCautionReason.SchedulingNotResponding]:
    "The candidate is not responding to the message for over 3 days.",
  [CandidateCautionReason.ReschedulingNotResponding]:
    "The candidate is not responding to the rescheduling message for over 3 days.",
  [CandidateCautionReason.WrongNumber]:
    "The person who answered the phone said that the number is wrong.",
  [CandidateCautionReason.UnknownReason]: "Unknown error occurred.",
  [CandidateCautionReason.FollowUpRequired]:
    "The candidate replied but scheduling wasn't completed, so a follow-up is needed.",
};

export class Candidate extends Utility<Candidate> {
  constructor(init?: Partial<CandidateWithContactStatusResponse>) {
    super();
    Object.assign(this, init);
  }

  get completed(): boolean {
    return this.status === CandidateStatus.Completed;
  }

  get cancelled(): boolean {
    return this.archived === true && this.completed === false;
  }

  get editable(): boolean {
    return this.archived === false && this.completed === false;
  }

  get cautious(): boolean {
    return (
      this.status === CandidateStatus.NotDelivered ||
      this.status === CandidateStatus.NeedToContact
    );
  }

  get status_string(): string | null {
    if (this.status === null) return null;
    if (this.status === CandidateStatus.NeedToContact)
      return "Follow-Up Required";
    if (this.status === CandidateStatus.Scheduled)
      return `Scheduled at ${this.local_call_time_datetime_string}`;
    return this.status
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  get local_call_time(): Date | null {
    if (this.call_time === null) return null;
    return this.get_local_time(this.call_time);
  }

  get local_call_time_date_string(): string | null {
    if (this.local_call_time === null) return null;
    return format(this.local_call_time, "MMM d, yyyy");
  }

  get local_call_time_datetime_string(): string | null {
    if (this.local_call_time === null) return null;
    return format(this.local_call_time, "MMM d, hh:mm aaa");
  }

  get local_call_time_system_date_string(): string | null {
    if (this.local_call_time === null) return null;
    return format(this.local_call_time, "yyyy-MM-dd");
  }

  get local_call_time_system_time_string(): string | null {
    if (this.local_call_time === null) return null;
    return format(this.local_call_time, "hh:mm aaa");
  }

  get local_call_time_since(): string | null {
    if (this.local_call_time === null) return null;
    return formatDistanceToNow(this.local_call_time, { addSuffix: true });
  }

  get display_name(): string {
    if (this.first_name && this.last_name)
      return `${this.first_name} ${this.last_name}`;
    if (this.first_name) return this.first_name;
    if (this.last_name) return this.last_name;
    return "";
  }

  get local_last_message_time(): Date | null {
    if (this.last_message_time === null) return null;
    return this.get_local_time(this.last_message_time);
  }

  get local_last_message_time_string(): string | null {
    if (this.local_last_message_time === null) return null;
    return format(this.local_last_message_time, "MMM d, hh:mm aaa");
  }

  get local_last_message_time_since(): string | null {
    if (this.local_last_message_time === null) return null;
    return formatDistanceToNow(this.local_last_message_time, {
      addSuffix: true,
    });
  }

  get no_reply_label(): string {
    const cautionReason = this.caution_reason?.toLocaleLowerCase() ?? "";
    // The candidate replied but scheduling never completed: it's a follow-up we owe them,
    // not a missing reply. Keep the day count but label it "No Update" instead of "No Reply".
    const isFollowUp = cautionReason === "follow_up_required";
    const formattedReason = cautionReason.startsWith("rescheduling")
      ? "Rescheduling"
      : cautionReason.startsWith("scheduling") || isFollowUp
        ? "Scheduling"
        : "";
    const prefix = isFollowUp ? "No Update" : "No Reply";

    if (!this.last_message_time) {
      return `${formattedReason ? "- " + formattedReason : ""}`;
    }

    const lastMessageDate = parseISO(this.last_message_time!);
    const currentDate = new Date();
    const days = differenceInDays(currentDate, lastMessageDate);
    return `${prefix} ${days < 10 ? days : "9+"} day${days > 1 ? "s" : ""} ${formattedReason ? "- " + formattedReason : ""}`;
  }

  get applying_positions_count(): number {
    return this.applying_positions?.length ?? 0;
  }

  get applying_positions_popover_message(): string {
    if (this.applying_positions && this.applying_positions.length > 0) {
      const positions = this.applying_positions;
      const leadText = `${this.applying_positions_count} concurrent applications:\n`;
      return `${leadText} ${positions.map((position, i) => `"${position}"${i === positions.length - 1 ? "" : ","} ${i % 2 === 1 && positions.length > 2 ? "\n" : ""}`).join(" ")}`;
    }
    return "";
  }

  isValidPhoneNumber(): boolean {
    if (!this.phone_number) return false;
    return parsePhoneNumber(this.phone_number)?.isValid() || false;
  }

  query(query: string): boolean {
    return this.display_name.toLowerCase().includes(query.toLowerCase());
  }

  isFuture(): boolean {
    return (
      this.local_call_time !== null && isAfter(this.local_call_time, new Date())
    );
  }

  isPast(): boolean {
    return (
      this.local_call_time !== null &&
      isBefore(this.local_call_time, new Date())
    );
  }

  toRequest(): CandidateRequest {
    return {
      resume_id: this.resume_id ?? null,
      first_name: this.first_name ?? null,
      last_name: this.last_name ?? null,
      phone_number: this.phone_number ?? null,
      sms_message: this.sms_message ?? null,
      archived: this.archived ?? null,
      call_time: this.call_time ?? null,
    };
  }

  toPatchRequest(original: Candidate): CandidatePatchRequest {
    const request: CandidatePatchRequest = {};
    if (this.resume_id !== original.resume_id)
      request.resume_id = this.resume_id;
    if (this.first_name !== original.first_name)
      request.first_name = this.first_name;
    if (this.last_name !== original.last_name)
      request.last_name = this.last_name;
    if (this.phone_number !== original.phone_number)
      request.phone_number = this.phone_number;
    if (this.sms_message !== original.sms_message)
      request.sms_message = this.sms_message;
    if (this.call_time_reminder_sent !== original.call_time_reminder_sent)
      request.call_time_reminder_sent = this.call_time_reminder_sent;
    if (this.withdrawn !== original.withdrawn)
      request.withdrawn = this.withdrawn;
    if (this.archived !== original.archived) request.archived = this.archived;
    if (this.call_time !== original.call_time)
      request.call_time = this.call_time;
    return request;
  }

  get local_evaluation_shared_at(): Date | null {
    if (this.evaluation_shared_at === null) return null;
    return this.get_local_time(this.evaluation_shared_at);
  }

  get local_evaluation_shared_at_string(): string | null {
    if (this.local_evaluation_shared_at === null) return null;
    return format(this.local_evaluation_shared_at, "MMM dd, yyyy hh:mm aa");
  }
}

export class CandidateFull extends Candidate implements CandidateFull {
  constructor(init?: Partial<CandidateFullResponse>) {
    super();
    Object.assign(this, init);

    if (init?.evaluation) this.evaluation = new Evaluation(init.evaluation);
  }

  get local_screened_at_date_string(): string | null {
    if (!this.screened_at) return null;
    return format(this.get_local_time(this.screened_at), "MMM d, yyyy");
  }
}
